/**
 * MER-157 stopgap (B), grain-safe since MER-500: cap forward-dated rows
 * on tables that emit them from advance-billed invoices.
 *
 * `financial_health` carries rows out to 2028-07 (Hattiesburg division)
 * because the FIFO allocator's advance-billing branch maps each future
 * service month to its own FH bucket. Every aggregation that doesn't
 * filter out future periods risks picking up a future-dated null row
 * (MER-155, AR Outstanding).
 *
 * THE BUG THIS MODULE FIXES (MER-500): the original cutoff was
 * `lte("period_start", "YYYY-MM")` -- a lexicographic compare on the
 * TEXT period column. Monthly rows hold bare "YYYY-MM" and pass, but
 * WEEKLY rows hold "YYYY-MM-DD" week starts, and every current-month
 * weekly row sorts AFTER the bare month key ('2026-06-01' > '2026-06'),
 * so the cutoff silently excluded ALL current-month weekly rows on every
 * NOW_CUTOFF table. The fix cuts on the real DATE column `period_date`
 * (on kpi_summary / route_kpis / financial_health since PR #105) with an
 * inclusive bound at the LAST day of the current month: the intent is to
 * exclude FUTURE periods, never the current month, at any grain.
 *
 * Pure and dependency-free so node:test imports it directly (the
 * tenantScope.mjs precedent). nowCutoff.d.mts types it for queries.ts.
 */

/** Tables that emit forward-dated rows and need the cap. */
export const NOW_CUTOFF_TABLES = new Set(["financial_health", "kpi_summary"]);

/**
 * The real DATE column the cutoff filters on. period_start stays TEXT
 * and grain-mixed (YYYY-MM monthly, YYYY-MM-DD weekly); period_date is
 * a true date at every grain (monthly rows anchor to the 1st).
 */
export const NOW_CUTOFF_COLUMN = "period_date";

/**
 * Inclusive upper bound for the cutoff: the last day of `now`'s month,
 * as an ISO date string. Built from local calendar parts (no
 * toISOString) so a UTC offset can't shift the month boundary.
 *
 * @param {Date} now
 * @returns {string} e.g. "2026-06-30"
 */
export function nowCutoffIsoBound(now) {
  const y = now.getFullYear();
  const monthIndex = now.getMonth();
  // Day 0 of the NEXT month is the last day of this month (leap-safe).
  const lastDay = new Date(y, monthIndex + 1, 0).getDate();
  return `${y}-${String(monthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
}

/**
 * Apply the now-cutoff to a PostgREST filter builder. Pure filter math:
 * tenant binding happens at the call sites via scopeTenantTable before
 * this runs (the queries.ts executors), so no tenant_id handling here.
 *
 * @param {{ lte: (column: string, value: unknown) => unknown }} query
 * @param {string} table  Resolved relation name.
 * @param {{ respectNowCutoff?: boolean, now?: Date }} [opts]
 *   respectNowCutoff: false opts a forecast widget out (manifest
 *   `respect_now_cutoff: false`); `now` is injectable for tests.
 * @returns the same builder, capped at the end of the current month.
 */
export function applyNowCutoffFilter(query, table, opts = {}) {
  if (!NOW_CUTOFF_TABLES.has(table)) return query;
  if (opts.respectNowCutoff === false) return query;
  const now = opts.now ?? new Date();
  return query.lte(NOW_CUTOFF_COLUMN, nowCutoffIsoBound(now));
}
